import { Policy, PolicyStatus, CloudPolicyType } from '../../types/policies';
import { Card, CardContent, CardHeader } from '../../../../ui/common-ui/card';
import { Badge } from '../../../../ui/common-ui/badge';
import { Button } from '../../../../ui/common-ui/button';
import { Switch } from '../../../../ui/common-ui/switch';
import { Checkbox } from '../../../../ui/common-ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../ui/common-ui/tooltip';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldOff,
  ChevronRight,
  Activity,
  Wrench,
  Loader2,
  Play,
  Trash2,
  OctagonAlert,
  CircleCheck,
} from 'lucide-react';
import { getPolicyCommands, normalizeExecutionHistory, normalizePolicySeverity } from '../../lib/policyTransforms';

interface PolicyCardProps {
  policy: Policy;
  onViewDetails: (policy: Policy) => void;
  onToggle: (id: string | number, enabled: boolean) => void;
  onDelete?: (policy: Policy) => void;
  showToggle?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (policy: Policy, selected: boolean) => void;
}

const getSeverityConfig = (severity: string) => {
  const configs: Record<string, { className: string; borderColor: string; dotColor: string; label: string }> = {
    Critical: {
      className: 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400',
      borderColor: 'border-l-red-500',
      dotColor: 'bg-red-500',
      label: 'Critical',
    },
    High: {
      className: 'bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400',
      borderColor: 'border-l-orange-500',
      dotColor: 'bg-orange-500',
      label: 'High',
    },
    Medium: {
      className: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600 dark:text-yellow-400',
      borderColor: 'border-l-yellow-500',
      dotColor: 'bg-yellow-500',
      label: 'Medium',
    },
    Low: {
      className: 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400',
      borderColor: 'border-l-green-500',
      dotColor: 'bg-green-500',
      label: 'Low',
    },
  };
  return configs[severity] || configs.Low;
};

const getCategoryConfig = (category: CloudPolicyType) => {
  const configs: Record<CloudPolicyType, { icon: any; label: string; className: string }> = {
    GUARDRAIL: {
      icon: ShieldCheck,
      label: 'Guardrail',
      className: 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400',
    },
    REMEDIATION: {
      icon: Wrench,
      label: 'Remediation',
      className: 'bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400',
    },
  };
  return configs[category] || configs.GUARDRAIL;
};

const getStatusConfig = (status: PolicyStatus, isRemediation: boolean) => {
  const configs: Record<PolicyStatus, { icon: any; label: string; className: string }> = {
    ACTIVE: {
      icon: ShieldCheck,
      label: isRemediation ? 'Armed' : 'Active',
      className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
    },
    INACTIVE: {
      icon: ShieldOff,
      label: 'Inactive',
      className: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
    },
    ENABLING: {
      icon: Loader2,
      label: 'Running',
      className: 'bg-primary/10 text-primary border-primary/30',
    },
    DISABLING: {
      icon: Loader2,
      label: 'Rolling back',
      className: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
    },
  };
  return configs[status] || configs.INACTIVE;
};

const isAllTargets = (targets?: string[]) => (
  Array.isArray(targets) && targets.some(target => target === '' || target === '*')
);

const getTargetSummary = (policy: Policy) => {
  if (isAllTargets(policy.includedUsers) || isAllTargets(policy.includedNHIs) || isAllTargets(policy.includedResources)) {
    return 'All targets';
  }
  const count = (policy.includedUsers?.length || 0) + (policy.includedNHIs?.length || 0) + (policy.includedResources?.length || 0);
  return count > 0 ? `${count} target${count === 1 ? '' : 's'}` : (policy.policyTargetTypes?.join(', ') || 'No target');
};

export const formatRelativeTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 30 ? `${days}d ago` : `${Math.floor(days / 30)}mo ago`;
};

export const PolicyCard = ({
  policy,
  onViewDetails,
  onToggle,
  onDelete,
  showToggle = true,
  selectable = false,
  selected = false,
  onSelectChange,
}: PolicyCardProps) => {
  const isRemediation = policy.cloudPolicyType === 'REMEDIATION';
  const severityConfig = getSeverityConfig(normalizePolicySeverity(policy.severity));
  const categoryConfig = getCategoryConfig(policy.cloudPolicyType);
  const statusConfig = getStatusConfig(policy.policyStatus, isRemediation);
  const CategoryIcon = categoryConfig.icon;
  const StatusIcon = statusConfig.icon;

  const runs = normalizeExecutionHistory(policy.executionHistory);
  const commands = getPolicyCommands(policy.policyEnableData);
  const linkedAlerts = Array.isArray(policy.linkedAlerts) ? policy.linkedAlerts : [];
  const actionLabel = commands[0]?.operation || commands[0]?.type || commands[0]?.sdkAction || 'Configured action';
  const isStatusTransitioning = policy.policyStatus === 'ENABLING' || policy.policyStatus === 'DISABLING';
  const isDeleteBlocked = policy.policyStatus === 'ACTIVE' || isStatusTransitioning;
  const lastFailed = policy.lastExecutionStatus === 'FAILED' && !isStatusTransitioning;
  const lastSucceeded = policy.lastExecutionStatus === 'SUCCESS' && !isStatusTransitioning;
  const lastRunAt = policy.lastExecutedAt || runs[0]?.finishedAt || runs[0]?.startedAt;
  const executeLabel = isRemediation ? (policy.policyStatus === 'ACTIVE' ? 'Armed' : 'Arm') : 'Execute';

  return (
    <Card
      className={`
        group relative bg-card overflow-hidden rounded-2xl border border-border border-l-4 ${severityConfig.borderColor}
        portal-card-shadow portal-card-hover
        ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}
      `}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {selectable && (
              <Checkbox
                checked={selected}
                onCheckedChange={checked => onSelectChange?.(policy, checked === true)}
                aria-label={`Select ${policy.title}`}
                className="h-[18px] w-[18px] rounded-md"
              />
            )}
            <Badge variant="outline" className={`${categoryConfig.className} w-fit font-medium text-xs`}>
              <CategoryIcon className="h-3 w-3 mr-1" />
              {categoryConfig.label}
            </Badge>
            <Badge variant="outline" className={`${severityConfig.className} w-fit font-medium text-xs`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${severityConfig.dotColor}`} />
              {severityConfig.label}
            </Badge>
            <Badge variant="outline" className={`${statusConfig.className} w-fit font-medium text-xs`}>
              <StatusIcon className={`h-3 w-3 mr-1 ${isStatusTransitioning ? 'animate-spin' : ''}`} />
              {statusConfig.label}
            </Badge>
            {lastFailed && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      role="img"
                      aria-label={`Last run failed: ${policy.lastExecutionReason || 'unknown reason'}`}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500 text-white shadow-[0_2px_6px_rgb(239_68_68/0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
                    >
                      <OctagonAlert className="h-3.5 w-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[280px] rounded-lg border-red-500/20 bg-card px-3 py-2 text-xs leading-relaxed text-foreground shadow-[0_8px_24px_rgb(15_23_42/0.12)]">
                    <p className="mb-0.5 font-semibold text-red-600">Last run failed</p>
                    <p className="text-muted-foreground">{policy.lastExecutionReason || 'The policy could not be applied.'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {showToggle && (
            <div className="relative flex shrink-0 items-center justify-end h-6 w-11">
              <div
                className={`absolute inset-0 flex items-center justify-end transition-all duration-300 ease-in-out ${
                  isStatusTransitioning ? 'opacity-100 scale-100 translate-x-0' : 'opacity-0 scale-75 translate-x-4 pointer-events-none'
                }`}
              >
                <Loader2 className="h-5 w-5 animate-spin text-primary mr-1" />
              </div>
              <div
                className={`transition-all duration-300 ease-in-out ${
                  isStatusTransitioning ? 'opacity-0 scale-75 -translate-x-4 pointer-events-none' : 'opacity-100 scale-100 translate-x-0'
                }`}
              >
                <Switch
                  checked={policy.policyStatus === 'ACTIVE'}
                  onCheckedChange={(checked) => policy.id !== undefined && onToggle(policy.id, checked)}
                  disabled={isStatusTransitioning}
                  className="data-[state=checked]:bg-primary transition-all duration-200"
                />
              </div>
            </div>
          )}
        </div>

        <h3 className="font-semibold text-base text-foreground leading-tight mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-200">
          {policy.title}
        </h3>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <ShieldAlert className="h-3 w-3" />
            <span>{policy.cloudType}</span>
          </div>
          {policy.policyTargetTypes && policy.policyTargetTypes.length > 0 && (
            <>
              <span className="text-border">/</span>
              <div className="flex flex-wrap items-center gap-1">
                {policy.policyTargetTypes.map((type, idx) => (
                  <span key={idx} className="bg-card px-1.5 py-0.5 rounded text-[10px] font-medium text-slate-600 dark:text-slate-300 border border-border/70 shadow-[0_1px_2px_rgb(15_23_42/0.04)]">
                    {type}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
          {policy.description}
        </p>

        {commands.length > 0 && (
          <div className="bg-card rounded-2xl p-3 space-y-2 border border-border shadow-[0_2px_8px_rgb(15_23_42/0.04)]">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                {isRemediation ? 'On alert' : 'Auto action'}
              </span>
              <Badge variant="outline" className="text-xs h-5 bg-card">
                {commands.length} {commands.length === 1 ? 'step' : 'steps'}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="font-mono bg-card px-2 py-0.5 rounded-lg border border-border/60 truncate max-w-[230px]">
                {String(actionLabel).split('.').pop()}
              </span>
              {commands.length > 1 && (
                <span className="font-semibold text-muted-foreground px-1">
                  +{commands.length - 1} more
                </span>
              )}
            </div>
          </div>
        )}

        {runs.length > 0 && (
          <div className="flex items-center gap-2 rounded-2xl border border-border bg-card px-3 py-2 shadow-[0_2px_8px_rgb(15_23_42/0.04)]">
            <Activity className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold text-foreground">{runs.length}</span>
            <span className="text-[11px] text-muted-foreground">{runs.length === 1 ? 'run' : 'runs'}</span>
            {lastRunAt && (
              <>
                <span className="text-border">│</span>
                <span className="text-[11px] text-muted-foreground">last {formatRelativeTime(lastRunAt)}</span>
              </>
            )}
            {(lastFailed || lastSucceeded) && (
              <span className={`ml-auto inline-flex items-center gap-1 text-[11px] font-medium ${lastFailed ? 'text-red-600' : 'text-emerald-600'}`}>
                {lastFailed ? <OctagonAlert className="h-3 w-3" /> : <CircleCheck className="h-3 w-3" />}
                {lastFailed ? 'Failed' : 'Succeeded'}
              </span>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline" className="text-xs h-5 bg-card">
            {getTargetSummary(policy)}
          </Badge>
          {linkedAlerts.length > 0 && (
            <Badge variant="outline" className="text-xs h-5 bg-card">
              {linkedAlerts.length} alert{linkedAlerts.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        <div className="flex gap-2 mt-3">
          <Button
            onClick={() => onViewDetails(policy)}
            variant="outline"
            className={`${showToggle ? 'flex-1' : 'w-full'} rounded-2xl group/btn border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200`}
          >
            <span>View Details / Edit</span>
            <ChevronRight className="h-4 w-4 ml-auto transition-transform group-hover/btn:translate-x-1" />
          </Button>

          {showToggle && (
            <Button
              onClick={() => policy.id !== undefined && onToggle(policy.id, true)}
              disabled={isStatusTransitioning || (isRemediation && policy.policyStatus === 'ACTIVE')}
              variant="default"
              className="rounded-2xl px-3 flex-shrink-0"
              title={isRemediation ? 'Arm the policy so it runs when a linked alert fires' : 'Execute policy'}
            >
              {isStatusTransitioning ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {executeLabel}
            </Button>
          )}

          {onDelete && (
            <Button
              onClick={() => onDelete(policy)}
              disabled={isDeleteBlocked}
              variant="outline"
              size="icon"
              className="rounded-2xl flex-shrink-0 border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all duration-200"
              title={isDeleteBlocked ? 'Disable the policy before deleting it' : 'Delete policy'}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
